import { motion } from 'framer-motion'
import Link from '../components/LocalizedLink'
import { Wrench, Upload, FileCheck, BookOpen, Download, ArrowRight } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import SEO from '../components/SEO'

const translations = {
  el: {
    hero: {
      title: 'Autodesk Inventor Plugin για Workshop Assistant',
      subtitle: 'Το add-in SheetMetalToJSON για Autodesk Inventor εξάγει δεδομένα εξαρτημάτων ελάσματος απευθείας από το assembly σας στο Workshop Assistant.',
      cta1: 'Δωρεάν Δοκιμή',
      cta2: 'Οδηγός Εγκατάστασης',
    },
    intro: {
      title: 'Τι κάνει το SheetMetalToJSON add-in',
      body: 'Το add-in SheetMetalToJSON για Autodesk Inventor εξάγει μεταδεδομένα εξαρτημάτων ελάσματος (υλικό, πάχος, διαστάσεις) απευθείας από το Inventor assembly σας, ώστε το Workshop Assistant να τα διαβάσει χωρίς να χρειάζεται ξεχωριστή εισαγωγή DXF ή STEP ανά εξάρτημα.',
    },
    steps: {
      title: 'Πώς λειτουργεί',
      items: [
        { icon: 'wrench', title: 'Εγκατάσταση Add-in', description: 'Εγκαταστήστε το add-in SheetMetalToJSON στο Autodesk Inventor ακολουθώντας τον αναλυτικό οδηγό.' },
        { icon: 'upload', title: 'Εξαγωγή από το Assembly', description: 'Εξάγετε μεταδεδομένα απευθείας από το ανοιχτό σας assembly ελάσματος με ένα κλικ.' },
        { icon: 'filecheck', title: 'Αυτόματη Ανάγνωση', description: 'Το Workshop Assistant διαβάζει τα εξαγόμενα δεδομένα και δημιουργεί εκτιμήσεις κόστους αυτόματα.' },
        { icon: 'book', title: 'Αναλυτικός Οδηγός', description: 'Πλήρης οδηγός εγκατάστασης και ρύθμισης διαθέσιμος στα Ελληνικά και Αγγλικά.' },
      ],
    },
    pricing: {
      title: 'Τιμή',
      monthly: { price: '€15', period: '/μήνα' },
      yearly: { price: '€150', period: '/έτος' },
      or: 'ή',
      note: 'Πρόσθετο στην άδεια Workshop Assistant.',
      cta: 'Επικοινωνήστε για Αγορά',
    },
    faq: [
      { q: 'Πόσο κοστίζει το Inventor plugin;', a: 'Το plugin κοστίζει €15 το μήνα ή €150 το χρόνο, ως πρόσθετο στην άδεια Workshop Assistant.' },
      { q: 'Χρειάζεται συγκεκριμένη έκδοση Inventor;', a: 'Δείτε τον αναλυτικό οδηγό εγκατάστασης στη σελίδα λήψης για τις τρέχουσες απαιτήσεις συμβατότητας.' },
      { q: 'Χρειάζεται να εξάγω DXF ξεχωριστά αν χρησιμοποιώ το add-in;', a: 'Όχι, το add-in εξάγει τα μεταδεδομένα απευθείας από το assembly, χωρίς να χρειάζεται ξεχωριστή εξαγωγή DXF.' },
    ],
    cta: { title: 'Συνδέστε το Inventor σας', subtitle: 'Κατεβάστε τη δωρεάν δοκιμή και τον οδηγό εγκατάστασης του add-in από τη σελίδα λήψης.', btn: 'Μετάβαση στη Λήψη' },
  },
  en: {
    hero: {
      title: 'Autodesk Inventor Plugin for Workshop Assistant',
      subtitle: 'The SheetMetalToJSON add-in for Autodesk Inventor exports sheet metal part data directly from your assembly into Workshop Assistant.',
      cta1: 'Download Free Trial',
      cta2: 'Installation Guide',
    },
    intro: {
      title: 'What the SheetMetalToJSON add-in does',
      body: 'The SheetMetalToJSON add-in for Autodesk Inventor exports sheet metal part metadata (material, thickness, dimensions) directly from your Inventor assembly, so Workshop Assistant can read it without a separate DXF or STEP import per part.',
    },
    steps: {
      title: 'How it works',
      items: [
        { icon: 'wrench', title: 'Install the Add-in', description: 'Install the SheetMetalToJSON add-in in Autodesk Inventor following the step-by-step guide.' },
        { icon: 'upload', title: 'Export from the Assembly', description: 'Export metadata directly from your open sheet metal assembly with one click.' },
        { icon: 'filecheck', title: 'Automatic Read-in', description: 'Workshop Assistant reads the exported data and generates cost estimates automatically.' },
        { icon: 'book', title: 'Detailed Guide', description: 'Full installation and setup guide available in English and Greek.' },
      ],
    },
    pricing: {
      title: 'Pricing',
      monthly: { price: '€15', period: '/month' },
      yearly: { price: '€150', period: '/year' },
      or: 'or',
      note: 'Add-on to your Workshop Assistant license.',
      cta: 'Contact Us to Buy',
    },
    faq: [
      { q: 'How much does the Inventor plugin cost?', a: 'The plugin costs €15 per month or €150 per year, as an add-on to your Workshop Assistant license.' },
      { q: 'Does it need a specific Inventor version?', a: 'See the detailed installation guide on the Download page for current compatibility requirements.' },
      { q: 'Do I still need to export DXF separately if I use the add-in?', a: 'No, the add-in exports metadata directly from the assembly, no separate DXF export needed.' },
    ],
    cta: { title: 'Connect Your Inventor', subtitle: "Download the free trial and the add-in's installation guide from the Download page.", btn: 'Go to Download' },
  },
}

const icons = { wrench: <Wrench className="h-10 w-10" />, upload: <Upload className="h-10 w-10" />, filecheck: <FileCheck className="h-10 w-10" />, book: <BookOpen className="h-10 w-10" /> }

const InventorPlugin = () => {
  const { lang } = useLanguage()
  const t = translations[lang]

  return (
    <div className="overflow-hidden">
      <SEO
        title="Autodesk Inventor Plugin — SheetMetalToJSON Add-in"
        description="Export sheet metal part data directly from Autodesk Inventor into Workshop Assistant with the SheetMetalToJSON add-in."
        path="/inventor-plugin"
        faq={t.faq}
      />

      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-5xl md:text-6xl font-bold mb-6">{t.hero.title}</motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-xl opacity-90 mb-8">{t.hero.subtitle}</motion.p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/download" className="flex items-center justify-center space-x-2 px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg">
                <Download className="h-5 w-5" /><span>{t.hero.cta1}</span>
              </Link>
              <Link to="/download" className="flex items-center justify-center space-x-2 px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-all duration-200">
                <span>{t.hero.cta2}</span><ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">{t.intro.title}</h2>
          <p className="text-lg text-gray-600">{t.intro.body}</p>
        </div>
      </section>

      <section className="section-container bg-gray-50">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">{t.steps.title}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {t.steps.items.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="card">
              <div className="text-primary-600 mb-4">{icons[f.icon]}</div>
              <h3 className="text-xl font-semibold mb-2">{f.title}</h3>
              <p className="text-gray-600">{f.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8">{t.pricing.title}</h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-4">
            <div className="flex items-baseline"><span className="text-5xl font-bold">{t.pricing.monthly.price}</span><span className="text-gray-500 ml-2">{t.pricing.monthly.period}</span></div>
            <span className="text-gray-400 text-lg">{t.pricing.or}</span>
            <div className="flex items-baseline"><span className="text-5xl font-bold">{t.pricing.yearly.price}</span><span className="text-gray-500 ml-2">{t.pricing.yearly.period}</span></div>
          </div>
          <p className="text-gray-600 mb-8">{t.pricing.note}</p>
          <Link to="/contact" className="inline-flex items-center space-x-2 px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-all duration-200 shadow-lg">
            <span>{t.pricing.cta}</span><ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      <section className="section-container bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center">FAQ</h2>
          <div className="space-y-4">
            {t.faq.map((item, i) => (
              <details key={i} className="card group">
                <summary className="font-semibold cursor-pointer list-none flex justify-between items-center">
                  {item.q}
                  <span className="text-primary-600 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-gray-600 mt-3">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section-container bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">{t.cta.title}</h2>
          <p className="text-xl mb-8 opacity-90">{t.cta.subtitle}</p>
          <Link to="/download" className="inline-flex items-center space-x-2 px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-all duration-200 shadow-lg">
            <Download className="h-5 w-5" /><span>{t.cta.btn}</span>
          </Link>
        </div>
      </section>
    </div>
  )
}

export default InventorPlugin
